const containerSelector = '[data-responsive-image]';
const imageSelector = '.responsive-image__image';
const initializedImages = new WeakSet<HTMLImageElement>();

const revealImage = async (image: HTMLImageElement): Promise<void> => {
    const container = image.closest<HTMLElement>(containerSelector);
    if (!container) return;

    try {
        await image.decode();
    } catch {
        // A successful load can still be displayed when decode() rejects.
    }

    container.classList.remove('is-pending');
    container.classList.add('is-loaded');
};

const initializeImage = (image: HTMLImageElement): void => {
    if (initializedImages.has(image)) return;
    initializedImages.add(image);

    if (image.complete) {
        if (image.naturalWidth > 0) void revealImage(image);
        return;
    }

    const container = image.closest<HTMLElement>(containerSelector);
    container?.classList.add('is-pending');
    image.addEventListener('load', () => void revealImage(image), { once: true });
    image.addEventListener(
        'error',
        () => {
            container?.classList.remove('is-pending');
        },
        { once: true },
    );
};

const initializeImages = (root: ParentNode): void => {
    if (root instanceof HTMLImageElement && root.matches(imageSelector)) {
        initializeImage(root);
    }
    root.querySelectorAll<HTMLImageElement>(imageSelector).forEach(initializeImage);
};

const initializeResponsiveImages = (): void => {
    initializeImages(document);

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLElement) initializeImages(node);
            });
        });
    });
    observer.observe(document.body, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeResponsiveImages, { once: true });
} else {
    initializeResponsiveImages();
}

document.addEventListener('hide.bs.modal', (event) => {
    if (event.target instanceof HTMLElement && event.target.matches('.modal')) {
        (document.activeElement as HTMLElement | null)?.blur();
    }
});
